// @ts-strict-ignore
import React from 'react';
import { IReviewImageModalProps } from './types';
import { ProfileImageSwiper } from '../ProfileImageSwiper';
import useMount from '../../../../hooks/useMount';
import { getReviewPhotoSummary } from '../../../../modules/Reviews';
import { IReviewPhotoSummary } from '../../../../types/ReviewTypes';
import { PortalOverlay, PortalOverlayModal } from '../../../ui';
import { IGalleryImage } from '../../../../api/Providers/GalleryImages';

const formatImageUrl = (image: { url: string }): string => `${image.url}_300x300.jpg`;

const LOAD_NEXT_PAGE_ZONE = 3; // load when three from the end

/**
 * This function fetches the next page of images and sets data
 * about it to states in the react component.
 * @param providerId
 * @param images
 * @param setImages
 * @param setNextPageUrl
 * @param nextPageUrl
 * @param setSwiperKey
 * @param setTotal
 */
async function getPageOfImages(
  providerId: number,
  images: IGalleryImage[],
  setImages: (images: IGalleryImage[]) => void,
  setNextPageUrl: (url: string) => void,
  nextPageUrl: string,
  setTotal: (total: number) => void,
): Promise<void> {
  //
  const reviewPhotoSummary: IReviewPhotoSummary = await getReviewPhotoSummary(
    providerId,
    nextPageUrl,
  );

  setTotal(reviewPhotoSummary.count);
  setNextPageUrl(reviewPhotoSummary.next);
  setImages([
    ...images,
    ...reviewPhotoSummary.results,
  ]);
}

/**
 * This component is the container which loads the images and manages the
 * carousel/swiper area where clients can view full size images.
 * @param provider
 * @param providerId
 * @param initialIndex
 * @param onCloseRequest
 * @param onReviewImageShown
 * @constructor
 */
export default function ReviewImageSummaryModalContainer({
  provider,
  providerId,
  initialIndex,
  onCloseRequest,
  onReviewImageShown,
}: IReviewImageModalProps) {
  //
  const [total, setTotal] = React.useState(0);
  const [images, setImages] = React.useState([]);
  const [nextPageUrl, setNextPageUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  useMount(() => {
    getPageOfImages(
      providerId,
      images,
      setImages,
      setNextPageUrl,
      nextPageUrl,
      setTotal,
    );
  });

  const handleIndexChange = React.useCallback(async (index: number) => {
    if (typeof onReviewImageShown === 'function') {
      onReviewImageShown({
        id: images[index].id,
        imageCount: total,
        imageIndex: index,
      });
    }

    if (!loading && index >= images.length - LOAD_NEXT_PAGE_ZONE && nextPageUrl) {
      setLoading(true);
      await getPageOfImages(
        providerId,
        images,
        setImages,
        setNextPageUrl,
        nextPageUrl,
        setTotal,
      );
      setLoading(false);
    }
  }, [
    images,
    total,
    loading,
    nextPageUrl,
    onReviewImageShown,
    providerId,
  ]);

  if (images.length === 0) {
    return null;
  }

  return (
    <PortalOverlay
      onBackdropClick={onCloseRequest}
      name="gallery-image-modal"
    >
      <PortalOverlayModal
        position="fullScreen"
        testID="ProfileImageSwiperContainer"
      >
        <ProfileImageSwiper
          images={images}
          provider={provider}
          imageTotal={total}
          initialIndex={initialIndex}
          onCloseRequest={onCloseRequest}
          formatImageUrl={formatImageUrl}
          onIndexChanged={handleIndexChange}
        />
      </PortalOverlayModal>
    </PortalOverlay>
  );
}
